import {
    useCallback,
    useRef,
    type FC,
    type ReactElement,
    type ChangeEvent,
    type FocusEvent,
    type KeyboardEvent,
    type MouseEvent,
    type FormEvent,
} from 'react';
import { defineMessages, useIntl } from 'dibs-react-intl';
import classnames from 'classnames';

import Close from 'dibs-icons/exports/Close.ts';
import ClearIcon from 'dibs-icons/exports/legacy/Close.ts';
import SearchIcon from 'dibs-icons/exports/legacy/MagnifyingGlass.ts';
import Search from 'dibs-icons/exports/Search.ts';
import ImageSearch from 'dibs-icons/exports/ImageSearch.ts';

// components
import { Link } from '../Link/index.tsx';
import { SIZES } from '../Common/constants.ts';
import { StyledButton } from '../Common/Button/StyledButton.tsx';
import { Input } from '../Input/index.tsx';
import { RotatingArrow } from '../RotatingArrow/index.tsx';
import { DropdownContent } from './DropdownContent.tsx';

// styles
import dibsCss from 'dibs-css';
import styles from './main.scss';

// types
import { type CustomButtonColors } from '../Button/index.tsx';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const { small, medium, large } = SIZES;

const labels = defineMessages({
    search: { id: 'dibsElements.search', defaultMessage: 'Search' },
    imageSearch: { id: 'dibsElements.imageSearch', defaultMessage: 'Search by image' },
    expandCollapseDropdown: {
        id: 'dibsElements.SearchBar.expandOrCollapseDropdown',
        defaultMessage: 'Expand or collapse dropdown',
    },
    clear: { id: 'dibsElements.SearchBar.clear', defaultMessage: 'Clear search terms' },
});

export type PropsType = {
    dataTn: string;
    value?: string;
    defaultValue?: string;
    onChange?: (value: string) => void;
    size?: typeof small | typeof medium | typeof large;
    autoComplete?: boolean | string;
    disabled?: boolean;
    focusOnClear?: boolean;
    hasRightSearchButton?: boolean;
    isClearButtonHidden?: boolean;
    noBorder?: boolean;
    onBlur?: (e: FocusEvent<HTMLInputElement>) => void;
    onClear?: (e: MouseEvent<HTMLElement>) => void;
    onFocus?: (e: FocusEvent<HTMLInputElement>) => void;
    onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void;
    onSubmit?: (e: FormEvent<HTMLFormElement>) => void;
    placeholder?: string;
    toggleDropdown?: () => void;
    dropdownIsOpen?: boolean;
    dropdownContent?: ReactElement;
    isHeaderSearch?: boolean;
    leftMargin?: typeof small;
    autoFocus?: boolean;
    openImageSearchModal?: () => void;
    customSearchIconColors?: CustomButtonColors;
    customClearIconColors?: CustomButtonColors;
    customDropdownIconColors?: CustomButtonColors;
};

const noop = (): void => {};

export const SearchBar: FC<PropsType> = props => {
    const {
        autoComplete = false,
        disabled = false,
        focusOnClear = false,
        hasRightSearchButton = true,
        noBorder = false,
        dropdownIsOpen = false,
        onBlur = noop,
        onClear = noop,
        onFocus = noop,
        onKeyDown = noop,
        dataTn,
        isClearButtonHidden,
        onChange,
        onSubmit,
        toggleDropdown,
        placeholder,
        size,
        value,
        defaultValue,
        dropdownContent,
        isHeaderSearch,
        leftMargin,
        autoFocus,
        openImageSearchModal,
        customSearchIconColors,
        customClearIconColors,
        customDropdownIconColors,
    } = props;
    const intl = useIntl();
    const inputRef = useRef<HTMLInputElement | null>(null);

    const doChange = useCallback(
        (val: string) => {
            if (onChange) {
                onChange(val);
            }
        },
        [onChange]
    );

    const onInputChangeHandler = (e: ChangeEvent<HTMLInputElement>): void => {
        doChange(e.target.value);
    };

    const handleOnBlur = useCallback(
        (e: FocusEvent<HTMLInputElement>) => {
            inputRef.current = e.target;
            if (onBlur) {
                onBlur(e);
            }
        },
        [onBlur, inputRef]
    );

    const handleOnClear = useCallback(
        (e: MouseEvent<HTMLElement>) => {
            if (
                inputRef &&
                inputRef.current &&
                typeof inputRef.current.focus === 'function' &&
                focusOnClear
            ) {
                inputRef.current.focus();
            }
            doChange('');
            onClear(e);
        },
        [doChange, onClear, inputRef, focusOnClear]
    );

    const hideClearButton =
        typeof isClearButtonHidden !== 'undefined' ? isClearButtonHidden : !value;

    const showRightSearchButton = hasRightSearchButton && !toggleDropdown;
    const showPrimarySearchButton = showRightSearchButton && (!hideClearButton || isHeaderSearch);

    // default to medium size
    let buttonSize = styles.mediumButton;
    let searchButtonSize = styles.mediumSearch;
    if (size === small) {
        buttonSize = styles.smallButton;
        searchButtonSize = styles.smallSearch;
    } else if (size === large) {
        buttonSize = styles.largeButton;
        searchButtonSize = styles.largeSearch;
    }

    const searchButton = (
        <div tabIndex={-1} data-tn={`${dataTn}-search-button-wrapper`}>
            <StyledButton
                ariaLabel={intl.formatMessage(labels.search)}
                type="submit"
                dataTn={`${dataTn}-search-button`}
                className={classnames({
                    [styles.defaultSearch]: !size,
                    [searchButtonSize]: !!size,
                    [styles.noClick]: hideClearButton,
                    [styles.fullyTransparent]:
                        (!isHeaderSearch && hideClearButton) || !showRightSearchButton,
                    [dibsCss.fillWhite]: showPrimarySearchButton,
                    [styles.roundedButton]: isHeaderSearch && showRightSearchButton,
                })}
                disabled={disabled}
                isPrimary={showPrimarySearchButton}
                roundedCorners={isHeaderSearch}
                customColors={customSearchIconColors}
            >
                {isHeaderSearch ? <Search variation="24" /> : <SearchIcon />}
            </StyledButton>
        </div>
    );

    const clearButton = (
        <div tabIndex={-1} data-tn={`${dataTn}-clear-wrapper`}>
            <StyledButton
                ariaLabel={intl.formatMessage(labels.clear)}
                type="button"
                dataTn={`${dataTn}-clear`}
                className={classnames(styles.fullyTransparent, {
                    [styles.defaultButton]: !size,
                    [buttonSize]: !!size,
                    [styles.hideButton]: hideClearButton,
                    [styles.showButton]: !hideClearButton,
                    [styles.clearButtonMargin]: !showRightSearchButton && isHeaderSearch && !value,
                    [styles.clearButtonMarginRemoved]:
                        !showRightSearchButton && isHeaderSearch && value,
                })}
                disabled={disabled}
                isPrimary={false}
                onClick={handleOnClear}
                tabIndex={hideClearButton ? -1 : undefined}
                customColors={customClearIconColors}
            >
                {isHeaderSearch ? <Close variation="20" /> : <ClearIcon />}
            </StyledButton>
        </div>
    );

    const imageSearchButton = openImageSearchModal && (
        <div
            className={classnames(
                dibsCss.relative,
                dibsCss.flex,
                dibsCss.justifyCenter,
                dibsCss.ml3px,
                showRightSearchButton ? dibsCss.mrSmall : dibsCss.mrSmaller
            )}
        >
            <Link
                ariaLabel={intl.formatMessage(labels.imageSearch)}
                dataTn={`${dataTn}-image-search-button`}
                onClick={e => {
                    e.stopPropagation(); // prevent unintentionally focusing the search bar
                    openImageSearchModal();
                }}
            >
                <ImageSearch variation="24" dataTn={`${dataTn}-image-search-icon`} />
            </Link>
        </div>
    );

    const rightSearchVariation = (
        <Input
            // base input fields
            autoComplete={autoComplete}
            autoCorrect={false}
            dataTn={dataTn}
            disabled={disabled}
            noBorder={noBorder}
            onBlur={handleOnBlur}
            onChange={onInputChangeHandler}
            onFocus={onFocus}
            onKeyDown={onKeyDown}
            placeholder={placeholder}
            size={size}
            spellCheck={false}
            value={value}
            defaultValue={defaultValue}
            title={intl.formatMessage(labels.search)}
            ariaLabel={intl.formatMessage(labels.search)}
            leftMargin={leftMargin}
            isHeaderInput={isHeaderSearch}
            // right variation fields
            rightDecorator={
                <>
                    {clearButton}
                    {imageSearchButton}
                    {searchButton}
                </>
            }
            rightDecoratorClass={classnames(styles.decoratorWrapper, {
                [styles.shiftRight]: !noBorder,
            })}
            autoFocus={autoFocus}
        />
    );

    const leftSearchVariation = (
        <Input
            // base input fields
            autoComplete={autoComplete}
            autoCorrect={false}
            dataTn={dataTn}
            disabled={disabled}
            noBorder={noBorder}
            onBlur={handleOnBlur}
            onChange={onInputChangeHandler}
            onFocus={onFocus}
            onKeyDown={onKeyDown}
            placeholder={placeholder}
            size={size}
            spellCheck={false}
            value={value}
            defaultValue={defaultValue}
            title={intl.formatMessage(labels.search)}
            ariaLabel={intl.formatMessage(labels.search)}
            isHeaderInput={isHeaderSearch}
            // left variation fields
            leftDecorator={searchButton}
            leftDecoratorClass={styles.decoratorWrapper}
            leftMargin={leftMargin}
            rightDecorator={
                toggleDropdown ? (
                    <>
                        {clearButton}
                        {imageSearchButton}
                        <StyledButton
                            ariaLabel={intl.formatMessage(labels.expandCollapseDropdown)}
                            type="button"
                            className={classnames(styles.fullyTransparent, buttonSize)}
                            dataTn={`${dataTn}-dropdown-button`}
                            disabled={disabled}
                            isPrimary={false}
                            onClick={(event: MouseEvent<HTMLElement>) => {
                                // stop propagation so that useOutsideClick does not also fire
                                event.nativeEvent.stopImmediatePropagation();
                                toggleDropdown();
                            }}
                            customColors={customDropdownIconColors}
                        >
                            <RotatingArrow direction={dropdownIsOpen ? 'up' : 'down'} />
                        </StyledButton>
                    </>
                ) : (
                    <>
                        {clearButton}
                        {imageSearchButton}
                    </>
                )
            }
            rightDecoratorClass={styles.decoratorWrapper}
            autoFocus={autoFocus}
        />
    );

    if (onSubmit) {
        return (
            <>
                <form data-tn={`${dataTn}-form`} onSubmit={onSubmit}>
                    <div className={styles.wrapper}>
                        {showRightSearchButton ? rightSearchVariation : leftSearchVariation}
                    </div>
                </form>
                {toggleDropdown && dropdownContent && (
                    <DropdownContent
                        dropdownContent={dropdownContent}
                        dropdownIsOpen={dropdownIsOpen}
                        toggleDropdown={toggleDropdown}
                    />
                )}
            </>
        );
    }

    const wrapperClassName = classnames(styles.wrapper, {
        [dibsCss.wFull]: isHeaderSearch,
    });

    return (
        <>
            <div className={wrapperClassName}>
                {showRightSearchButton ? rightSearchVariation : leftSearchVariation}
            </div>
            {toggleDropdown && dropdownContent && (
                <DropdownContent
                    dropdownContent={dropdownContent}
                    dropdownIsOpen={dropdownIsOpen}
                    toggleDropdown={toggleDropdown}
                />
            )}
        </>
    );
};
